"use client";
import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import AuthModal from "./AuthModal";
import IconifyIcon from "./IconifyIcon";

export default function LandingPage() {
  const [showAuthModal, setShowAuthModal] = useState(false);

  return (
    <div className="bg-black text-slate-400 antialiased min-h-screen min-h-[100dvh] scroll-smooth" style={{ backgroundColor: "#000" }}>
      <LandingNav onLoginClick={() => setShowAuthModal(true)} />
      <main className="relative pt-24 sm:pt-32 pb-20 overflow-hidden">
        <HeroSection />
        <WhySection />
        <ProductSection />
        <ProductTour />
        <AIChatSection />
        <HowItWorks />
        <PricingSection />
        <CTABand />
        <FAQSection />
      </main>
      <LandingFooter />
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
}

function LandingNav({ onLoginClick }: { onLoginClick: () => void }) {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-black bg-black backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded bg-gradient-to-tr from-[#14FF76] to-[#00C24E] flex items-center justify-center">
            <span className="text-[#050B14] font-bold text-sm">CP</span>
          </div>
          <span className="text-white font-medium tracking-tight">CourtPicks AI</span>
        </Link>
        <div className="hidden md:flex items-center gap-8 text-sm font-medium">
          <a href="#product" className="hover:text-white transition-colors">Product</a>
          <a href="#live" className="hover:text-white transition-colors">Live</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onLoginClick}
            className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
          >
            Login
          </button>
          <Link href="/signup" className="hidden sm:inline-flex h-9 items-center justify-center rounded bg-white px-4 text-sm font-medium text-[#050B14] hover:bg-slate-200 transition-colors">
            Start your 3-day free trial
          </Link>
        </div>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="max-w-4xl mx-auto px-6 text-center relative z-10">
      <div className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-black px-3 py-1 text-xs font-medium text-[#14FF76] mb-8">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#14FF76] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#14FF76]"></span>
        </span>
        Decision-support for every matchup
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white tracking-tight leading-[1.1]">Make better NBA betting decisions faster.</h1>
      <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">CourtPicks puts the key context for every matchup in one place: recent form, injuries, live box score, and model-vs-market signals.</p>
      <div className="mt-10">
        <Link href="/signup" className="inline-flex h-12 px-8 rounded bg-[#14FF76] text-[#050B14] font-medium items-center justify-center hover:bg-[#00C24E] transition-colors">Start your 3-day free trial</Link>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section className="max-w-7xl mx-auto px-6 mt-16 sm:mt-20">
      <div className="rounded-2xl border border-white/[0.08] bg-black px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs font-medium text-[#14FF76]">Why CourtPicks</div>
            <div className="mt-1 text-white font-medium tracking-tight">Most bad bets are missing context.</div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-slate-400">
            <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#14FF76]/80"></span>Late injury updates</li>
            <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#14FF76]/80"></span>Recent form matters</li>
            <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#14FF76]/80"></span>Live pace and scoring</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function ProductSection() {
  return (
    <section id="product" className="max-w-7xl mx-auto px-6 mt-24 sm:mt-32">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="relative rounded-2xl border border-white/[0.08] bg-black p-2 shadow-2xl overflow-hidden">
          <div className="aspect-[4/3] relative rounded-xl">
            <Image
              src="/dashboardpic.png"
              alt="CourtPicks dashboard screenshot"
              fill
              className="object-cover object-top rounded-xl"
              sizes="(max-width: 1024px) 100vw, 50vw"
              priority
            />
          </div>
        </div>
        <div>
          <h2 className="text-3xl font-medium text-white tracking-tight mb-6">The full picture, at a glance.</h2>
          <p className="text-lg text-slate-400 mb-8">Everything you need to read a matchup clearly.</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-3"><span className="text-[#14FF76]">&#10003;</span><span className="text-slate-300">Last 3 games (team + key player performance)</span></li>
            <li className="flex items-start gap-3"><span className="text-[#14FF76]">&#10003;</span><span className="text-slate-300">Injuries and game-time decisions</span></li>
            <li className="flex items-start gap-3"><span className="text-[#14FF76]">&#10003;</span><span className="text-slate-300">Matchup signals: pace, PPP strength, momentum</span></li>
            <li className="flex items-start gap-3"><span className="text-[#14FF76]">&#10003;</span><span className="text-slate-300">Market context: consensus line / blended view</span></li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function ProductTour() {
  return (
    <section className="max-w-7xl mx-auto px-6 mt-24 sm:mt-32">
      <h2 className="text-2xl font-medium text-white tracking-tight mb-12">What you see on every game</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="rounded-xl border border-white/[0.08] bg-black p-6">
          <h3 className="text-white font-medium mb-2">Game Analysis</h3>
          <p className="text-sm text-slate-400">Last 3 games, injury impact, and key matchup signals.</p>
        </div>
        <div id="live" className="rounded-xl border border-white/[0.08] bg-black p-6">
          <h3 className="text-white font-medium mb-2">Live View</h3>
          <p className="text-sm text-slate-400">Live scoring, quarter breakdowns, box score polling.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-black p-6">
          <h3 className="text-white font-medium mb-2">Totals Explained</h3>
          <p className="text-sm text-slate-400">Capacity projection with factor breakdown.</p>
        </div>
      </div>
    </section>
  );
}

function AIChatSection() {
  return (
    <section className="max-w-7xl mx-auto px-6 mt-24 sm:mt-32">
      <div className="rounded-2xl border border-white/[0.08] bg-black p-1">
        <div className="rounded-xl bg-black p-8 md:p-12 flex flex-col md:flex-row items-center gap-10">
          <div className="flex-1">
            <h2 className="text-3xl font-medium text-white tracking-tight mb-4">Challenge the read before you commit.</h2>
            <p className="text-slate-400 leading-relaxed mb-6">Ask why, test alternate angles, and stress-check assumptions.</p>
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-black border border-white/[0.05] text-sm text-slate-300">AI Decision Partner included (150 interactions/mo)</div>
          </div>
          <div className="w-full md:w-96 rounded-xl border border-white/[0.1] bg-black p-4 flex flex-col gap-3 shadow-2xl">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-black border border-white/[0.1] shrink-0"></div>
              <div className="p-3 rounded-2xl rounded-tl-none bg-black border border-white/[0.08] text-xs text-slate-300">How does the injury affect pace tonight?</div>
            </div>
            <div className="flex gap-3 flex-row-reverse">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#14FF76] to-[#00C24E] flex items-center justify-center text-[#050B14] text-sm shrink-0">AI</div>
              <div className="p-3 rounded-2xl rounded-tr-none bg-[#14FF76]/10 border border-[#14FF76]/20 text-xs text-[#14FF76]">Pace increases by +2.4 possessions historically.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="max-w-7xl mx-auto px-6 mt-24 sm:mt-32">
      <h2 className="text-2xl font-medium text-white tracking-tight text-center mb-16">How it works</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-black border border-white/[0.2] flex items-center justify-center text-white font-medium mb-4">1</div>
          <h3 className="text-white font-medium mb-2">Scan</h3>
          <p className="text-sm text-slate-500">Today/Tomorrow slate</p>
        </div>
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-black border border-white/[0.2] flex items-center justify-center text-white font-medium mb-4">2</div>
          <h3 className="text-white font-medium mb-2">Open</h3>
          <p className="text-sm text-slate-500">A matchup</p>
        </div>
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-black border border-white/[0.2] flex items-center justify-center text-white font-medium mb-4">3</div>
          <h3 className="text-white font-medium mb-2">Verify</h3>
          <p className="text-sm text-slate-500">Injuries + context</p>
        </div>
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-black border border-[#14FF76] flex items-center justify-center text-[#14FF76] font-medium mb-4">4</div>
          <h3 className="text-white font-medium mb-2">Make the call</h3>
          <p className="text-sm text-slate-500">Faster, more informed</p>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 mt-24 sm:mt-32">
      <div className="max-w-3xl mx-auto text-center mb-12">
        <h2 className="text-3xl font-medium text-white tracking-tight mb-4">Season Licenses</h2>
        <div className="text-lg text-white mb-3">One-time season license. <span className="text-[#14FF76] font-medium">No subscription</span>. No auto-renew.</div>
        <p className="text-slate-400 max-w-2xl mx-auto">See how structured game analysis helps you separate <span className="text-slate-200">clear edges</span> from <span className="text-slate-200">tight lines</span> before placing a bet.</p>
      </div>
      <div className="max-w-2xl mx-auto rounded-2xl border border-white/[0.1] bg-black overflow-hidden relative text-left">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#14FF76] to-[#00C24E]"></div>
        <div className="p-6 sm:p-10">
          <div className="text-center mb-10 border-b border-white/[0.06] pb-8">
            <h3 className="text-2xl font-medium text-white mb-2">Choose your season license</h3>
            <p className="text-white text-sm mb-6">Includes AI Decision Partner (150 interactions/mo).</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto text-left">
              <div className="rounded-xl border border-white/[0.08] bg-black p-5">
                <div className="text-xs font-medium text-[#14FF76] mb-2">Limited</div>
                <div className="text-white font-medium">Founding Season License — $199</div>
                <div className="text-slate-400 text-xs mt-2 leading-relaxed">Valid through the end of the 2025–2026 NBA Regular Season</div>
                <div className="text-slate-400 text-xs mt-1 leading-relaxed">Available for the first 10 subscribers</div>
              </div>
              <div className="rounded-xl border border-white/[0.08] bg-black p-5">
                <div className="text-xs font-medium text-slate-400 mb-2">Standard</div>
                <div className="text-white font-medium">Regular Season License — $299</div>
                <div className="text-slate-400 text-xs mt-2 leading-relaxed">Valid through the end of the 2025–2026 NBA Regular Season</div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-lg mx-auto mt-6">CourtPicks gives you clear, real-time context so you know <span className="text-white">when to act, when to wait, and when to pass</span>.</p>
          </div>
          <div className="mb-10">
            <h4 className="text-xs font-medium text-slate-500 uppercase tracking-widest mb-6 text-center">What You Get</h4>
            <ul className="space-y-6">
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:graph-new-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">Live Edge Engine (Spread & Total)</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">Real-time model vs market comparison showing <span className="text-slate-300">where an edge exists</span> and how strong it is — clearly separating <span className="text-slate-300">clear edges</span> from <span className="text-slate-300">tight lines</span>.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:stopwatch-play-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">Live Box Score & Quarter Flow</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">Current score, quarter splits, clock, and top performers so you can <span className="text-slate-300">monitor momentum and spot market overreactions</span> as the game unfolds.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:filter-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">Auto Picks with Reliability Filters</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">Spreads and totals surface <span className="text-slate-300">only when they meet your criteria</span> (edge strength, reliability, timing). No forced picks. No noise.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:battery-charge-minimalistic-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">Capacity Total Model</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">Scoring-capacity projection vs market total to help identify <span className="text-slate-300">overpriced or underpriced totals</span>, especially when early scoring distorts perception.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:medical-kit-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">Injury & Availability Feed</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">Key outs and rotation-impact notes so you can gauge <span className="text-slate-300">volatility and uncertainty</span> before betting — or decide to wait.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:history-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">Recent Form Snapshot</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">Last 3 games, estimated totals, and momentum context as a <span className="text-slate-300">quick reality check</span> against misleading narratives.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:magnifer-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">Matchup Intelligence</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">Pace, spacing, defensive style, position matchups, PPP and tempo metrics — providing context on <span className="text-slate-300">why an edge exists or why it's thin</span>.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:eye-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">Edge Clarity, Not Forced Action</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">CourtPicks highlights when the edge is <span className="text-slate-300">clear</span> and when it's <span className="text-slate-300">tight</span>. Tight edges are signals to <span className="text-slate-300">wait, watch live, or skip</span>, not commands to bet.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#14FF76]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#14FF76]">
                  <IconifyIcon icon="solar:chat-square-code-linear" width="14" />
                </div>
                <div>
                  <h5 className="text-white font-medium text-sm">AI Assistant (Optional Add-On)</h5>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 leading-relaxed">For close games, use AI to ask <span className="text-slate-300 italic">"why is this tight?"</span>, debate spread or total logic, and think through timing. AI explains reasoning based on the same on-page data — it <span className="text-white">does not</span> predict outcomes or guarantee wins.</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="pt-8 border-t border-white/[0.08]">
            <h4 className="text-center text-white font-medium mb-6">Simple, Honest Pricing</h4>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-8">
              <div className="text-center"><div className="text-white font-medium text-sm">$199 or $299</div><div className="text-xs text-slate-500">One-time</div></div>
              <div className="text-center"><div className="text-white font-medium text-sm">No subscription</div><div className="text-xs text-slate-500">No auto-renew</div></div>
              <div className="text-center"><div className="text-white font-medium text-sm">No hidden fees</div><div className="text-xs text-slate-500">Transparent</div></div>
              <div className="text-center"><div className="text-white font-medium text-sm">No hype</div><div className="text-xs text-slate-500">Just tools</div></div>
            </div>
            <Link href="/signup" className="block w-full py-4 rounded bg-[#14FF76] text-[#050B14] font-bold text-center hover:bg-[#00C24E] transition-colors">Start your 3-day free trial</Link>
            <p className="mt-4 text-center text-xs text-slate-600 leading-relaxed">CourtPicks is not a tip service. It's a tool to help you make <span className="text-slate-500">better decisions under uncertainty</span>.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTABand() {
  return (
    <section className="max-w-7xl mx-auto px-6 mt-24 mb-16 text-center">
      <div className="rounded-2xl border border-white/[0.08] bg-black px-6 py-12 md:py-20 relative overflow-hidden">
        <h2 className="text-3xl sm:text-4xl font-medium text-white tracking-tight mb-6">Make the call with the full picture.</h2>
        <Link href="/signup" className="inline-flex px-8 py-3 rounded bg-[#14FF76] text-[#050B14] font-medium hover:bg-[#00C24E] transition-colors">Start your 3-day free trial</Link>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    { q: "Is this a guaranteed picks service?", a: "No. CourtPicks is a decision tool that organizes context and signals." },
    { q: "Do signals update before tipoff?", a: "Yes. Injury news and market movement can change the read." },
    { q: "What counts as an AI chat?", a: "One interaction equals one question and one response." },
    { q: "Can I use it live?", a: "Yes. Live view + Live Engine are built for in-game context." },
  ];
  return (
    <section id="faq" className="max-w-3xl mx-auto px-6 mb-24">
      <h2 className="text-2xl font-medium text-white tracking-tight mb-8">FAQ</h2>
      <div className="space-y-4">
        {faqs.map((f) => (
          <div key={f.q} className="p-4 rounded-lg border border-white/[0.05] bg-black">
            <h3 className="text-sm font-medium text-white">{f.q}</h3>
            <p className="mt-2 text-sm text-slate-400">{f.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="border-t border-white/[0.06] bg-black py-12">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded bg-gradient-to-tr from-[#14FF76] to-[#00C24E] flex items-center justify-center"><div className="w-1.5 h-1.5 rounded-full bg-black"></div></div>
            <span className="text-white font-medium">CourtPicks AI</span>
          </div>
          <p className="text-xs text-slate-500">For informational purposes only.</p>
        </div>
        <div className="flex gap-6 text-sm text-slate-400">
          <a href="#" className="hover:text-white transition-colors">Terms</a>
          <a href="#" className="hover:text-white transition-colors">Privacy</a>
          <a href="#" className="hover:text-white transition-colors">Contact</a>
        </div>
      </div>
    </footer>
  );
}
